import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Portal,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  addEmployeeWorkHours,
  executeEmployeeDeactivation,
  fetchAllEmployees,
  fetchEmployeeById,
  fetchServices,
  executeEmployeeDeletion,
  EmployeeDeactivationPreview,
  EmployeeDeletionPreview,
  generateRecurringBreakBlockersForEmployee,
  getCurrentBusinessConfig,
  getEmployeeWorkHours,
  isRecurringBreakPartialFailureError,
  previewEmployeeDeactivation,
  previewEmployeeDeletion,
  updateEmployee,
  updateEmployeeSchedulesForDates,
  type GenerateRecurringBreakBlockersResult,
} from '../../firebase/firebase-delegate';
import { Appointment, BitStringData, Employee, RecurringBreak, Service, WorkHours } from '../../types';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase/firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK, DAYS_OF_WEEK_PREFERRED_ORDER, EMPLOYEE_BIO_MAX_LENGTH } from '../../constants';
import { dayjs } from '../../utils/dayjs-config';
import {
  formatMinutes,
  extractTimesFromBitString,
  generateScheduleBitString,
} from '../../time/timeUtils';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import ImageSelect from '../../components/global/images/ImageSelector';
import { tenantStoragePath } from '../../tenancy/paths';
import { useTenant } from '../../tenancy/TenantContext';
import { editDialogContentSx, editDialogPaperSx } from '../../components/global/editDialogLayout';
import { hasDuplicateEmployeeEmail, normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';
import { applyEmployeeSaveCache, reconcileEmployeeCachesFromSource } from '../../utils/cache-delegate';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { useAdminData } from '../../contexts/AdminDataContext';
import LoadingOverlay from '../../components/global/LoadingOverlay';

const areWorkHoursEqual = (left: WorkHours, right: WorkHours): boolean =>
  DAYS_OF_WEEK_PREFERRED_ORDER.every((day) => left[day] === right[day]);

const parseFullName = (value: string): { firstName: string; lastName: string } => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const recurringBreakSortKey = (definition: RecurringBreak): string => (
  [
    definition.id,
    definition.weekday,
    definition.startTime,
    definition.endTime,
    definition.repeatEveryXWeeks,
    definition.repeatUntilDate || '',
    definition.indefinite ? '1' : '0',
  ].join('|')
);

const areRecurringBreakSetsEqual = (
  left: RecurringBreak[],
  right: RecurringBreak[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const normalizedLeft = left.map(recurringBreakSortKey).sort();
  const normalizedRight = right.map(recurringBreakSortKey).sort();
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const CACHE_STALE_TIME_MS = 1000 * 60 * 5;
const MISSING_WORK_HOURS_TEMPLATE_ERROR = 'No schedule template found';
type WorkHoursTemplateStatus = 'idle' | 'loaded' | 'missing_template' | 'error';
type ManageSectionKey = 'profile' | 'services' | 'work_hours' | null;

const EmployeeAvatar: React.FC<{ employee: Employee }> = ({ employee }) => {
  const photo = useQuery({
    queryKey: ['employee-photo-url', employee.profilePictureImagePath],
    queryFn: () => getDownloadURL(ref(storage, employee.profilePictureImagePath!)),
    enabled: Boolean(employee.profilePictureImagePath),
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
  const initials = `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  return (
    <Avatar src={photo.data} alt="" sx={{ width: 52, height: 52, fontWeight: 900, bgcolor: 'rgba(25,118,210,.12)', color: 'primary.main' }}>
      {initials}
    </Avatar>
  );
};

const SwitchRow: React.FC<{
  label: string;
  detail: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, detail, checked, disabled, onChange }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
    sx={{ p: 1.75, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
    <Box>
      <Typography fontWeight={850}>{label}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Box>
    <Switch checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} inputProps={{ 'aria-label': label }} />
  </Stack>
);

const ManageEmployeesPage: React.FC = () => {
  const { tenantId } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentEmployeeQuery } = useAdminData();

  const employeesQuery = useQuery<Employee[]>({
    queryKey: ['allEmployees', tenantId],
    queryFn: () => fetchAllEmployees(tenantId),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const servicesQuery = useQuery<Service[]>({
    queryKey: ['services', tenantId],
    queryFn: () => fetchServices(tenantId),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [active, setActive] = useState(true);
  const [profilePictureImagePath, setProfilePictureImagePath] = useState<string | undefined>(undefined);
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [expandedManageSection, setExpandedManageSection] = useState<ManageSectionKey>('profile');
  const [workHoursTemplateStatus, setWorkHoursTemplateStatus] = useState<WorkHoursTemplateStatus>('idle');
  const businessHoursWorkHours = useMemo(
    () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
    [businessConfigQuery.data?.businessInfo?.businessHours]
  );
  const [workHours, setWorkHours] = useState<WorkHours>(businessHoursWorkHours);
  const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>)
  );
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultSeverity, setResultSeverity] = useState<'success' | 'warning' | 'error' | null>(null);
  const [saveSuccessDialogOpen, setSaveSuccessDialogOpen] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [deleteSuccessDialogOpen, setDeleteSuccessDialogOpen] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deletePreviewData, setDeletePreviewData] = useState<EmployeeDeletionPreview | null>(null);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);
  const [deleteTargetEmployeeId, setDeleteTargetEmployeeId] = useState<string | null>(null);
  const [deleteTargetEmployeeName, setDeleteTargetEmployeeName] = useState('');
  const deletePreviewRequestIdRef = useRef(0);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivatePreviewData, setDeactivatePreviewData] = useState<EmployeeDeactivationPreview | null>(null);
  const [deactivatePreviewError, setDeactivatePreviewError] = useState<string | null>(null);
  const deactivatePreviewRequestIdRef = useRef(0);
  const previousDialogEmployeeIdRef = useRef<string | null>(null);
  const [needsRefreshOnDialogClose, setNeedsRefreshOnDialogClose] = useState(false);

  const templateEndDate = dayjs().add(3, 'month');

  const normalizeSocialHandle = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
    const withoutDomain = withoutProtocol
      .replace(/^www\./i, '')
      .replace(/^instagram\.com\//i, '')
      .replace(/^x\.com\//i, '')
      .replace(/^twitter\.com\//i, '');
    return withoutDomain.replace(/^@+/, '').split('/')[0].split('?')[0].trim();
  };

  const employeeWorkHoursQuery = useQuery<WorkHours>({
    queryKey: ['manageEmployeeWorkHours', tenantId, selectedEmployee?.id],
    queryFn: () => getEmployeeWorkHours(selectedEmployee!.id, tenantId),
    enabled: Boolean(selectedEmployee?.id && openDialog),
    staleTime: CACHE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const isWorkHoursLoadingForSelection =
    Boolean(selectedEmployee?.id && openDialog) &&
    (employeeWorkHoursQuery.isPending || employeeWorkHoursQuery.isFetching);

  useEffect(() => {
    if (!selectedEmployee || !openDialog) return;

    const isDifferentEmployee = previousDialogEmployeeIdRef.current !== selectedEmployee.id;
    if (isDifferentEmployee) {
      setResultMessage(null);
      setResultSeverity(null);
    }
    previousDialogEmployeeIdRef.current = selectedEmployee.id;

    setFirstName(selectedEmployee.firstName || '');
    setLastName(selectedEmployee.lastName || '');
    setEmail(selectedEmployee.email || '');
    setPhone(selectedEmployee.phone || '');
    setBio((selectedEmployee.bio || '').slice(0, EMPLOYEE_BIO_MAX_LENGTH));
    setInstagram(normalizeSocialHandle(selectedEmployee.socials?.instagram || ''));
    setFacebook(selectedEmployee.socials?.facebook || '');
    setTwitter(normalizeSocialHandle(selectedEmployee.socials?.twitter || ''));
    setIsAdmin(
      selectedEmployee.id === (businessConfigQuery.data?.paymentInfo?.owner || '')
        ? true
        : (selectedEmployee.admin || false)
    );
    setActive(selectedEmployee.active !== false);
    setResultMessage(null);
    setResultSeverity(null);
    setDeleteDialogOpen(false);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    setDeleteTargetEmployeeId(null);
    setDeleteTargetEmployeeName('');
    deletePreviewRequestIdRef.current += 1;
    setDeactivateDialogOpen(false);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
    deactivatePreviewRequestIdRef.current += 1;
    setProfilePictureImagePath(selectedEmployee.profilePictureImagePath);
    setProfilePictureFile(undefined);
    setExpandedManageSection('profile');
    setWorkHoursTemplateStatus('idle');

    const assignedIds = (servicesQuery.data ?? [])
      .filter((service) => service.employees.includes(selectedEmployee.id))
      .map((service) => service.id);
    setSelectedServiceIds(new Set(assignedIds));

    const initialBreakTemplates = DAYJS_DAYS_OF_WEEK.reduce((acc, day, index) => {
      acc[day] = (selectedEmployee.recurringBreaks ?? [])
        .filter((definition) => definition.weekday === index)
        .map((definition) => ({ ...definition }));
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>);
    setRecurringBreakTemplates(initialBreakTemplates);
  }, [openDialog, selectedEmployee, servicesQuery.data, businessConfigQuery.data?.paymentInfo?.owner]);

  useEffect(() => {
    if (openDialog) return;

    previousDialogEmployeeIdRef.current = null;
    setResultMessage(null);
    setResultSeverity(null);
  }, [openDialog]);

  useEffect(() => {
    if (!selectedEmployee || !openDialog) return;

    if (employeeWorkHoursQuery.isPending || employeeWorkHoursQuery.isFetching) {
      setWorkHoursTemplateStatus('idle');
      return;
    }

    if (employeeWorkHoursQuery.data) {
      setWorkHours(employeeWorkHoursQuery.data);
      setWorkHoursTemplateStatus('loaded');
      return;
    }

    if (employeeWorkHoursQuery.isError) {
      const errorMessage = employeeWorkHoursQuery.error instanceof Error
        ? employeeWorkHoursQuery.error.message
        : '';
      if (errorMessage.includes(MISSING_WORK_HOURS_TEMPLATE_ERROR)) {
        setWorkHours(businessHoursWorkHours);
        setWorkHoursTemplateStatus('missing_template');
      } else {
        setWorkHoursTemplateStatus('error');
      }
    }
  }, [
    employeeWorkHoursQuery.data,
    employeeWorkHoursQuery.error,
    employeeWorkHoursQuery.isError,
    employeeWorkHoursQuery.isFetching,
    employeeWorkHoursQuery.isPending,
    openDialog,
    selectedEmployee,
    businessHoursWorkHours,
  ]);

  const flattenRecurringBreaks = () =>
    DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
      (recurringBreakTemplates[day] || []).map((definition) => ({
        ...definition,
        weekday: index,
      }))
    );

  const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
    setWorkHours((prev) => ({
      ...prev,
      [day]: generateScheduleBitString(updatedSchedule),
    }));
  };

  const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
    setRecurringBreakTemplates((prev) => ({
      ...prev,
      [day]: breaks,
    }));
  };

  const toggleService = (serviceId: string) => {
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleProfilePictureChange = (newPicture?: File) => {
    setProfilePictureFile(newPicture);
    if (!newPicture) {
      setProfilePictureImagePath(undefined);
      return;
    }
    setProfilePictureImagePath((previous) => {
      if (previous) {
        return previous;
      }
      if (!selectedEmployee) {
        return undefined;
      }
      return tenantStoragePath(tenantId, `employees/${selectedEmployee.id}/profilePicture`);
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (input?: { deactivationStrategy?: 'handoff' | 'cancel' }) => {
      if (!selectedEmployee) throw new Error('No employee selected.');
      if (isWorkHoursLoadingForSelection) {
        throw new Error('Work hours are still loading. Please wait a moment and try again.');
      }
      const normalizedEmail = normalizeEmail(email);
      const existingEmployees = employeesQuery.data ?? [];
      if (!isResourceMode) {
        const duplicateEmail = hasDuplicateEmployeeEmail(existingEmployees, normalizedEmail, selectedEmployee.id);
        if (duplicateEmail) {
          throw new Error('An employee already exists with this email.');
        }
      }
      if (!isResourceMode && selectedEmployee.isOrchestrated !== true) {
        const currentEmail = normalizeEmail(selectedEmployee.email || '');
        if (normalizedEmail !== currentEmail) {
          throw new Error('Email cannot be changed for staff members with login access.');
        }
      }

      const isDeactivatingNow = selectedEmployee.active !== false && active === false;
      if (!firstName.trim()) {
        throw new Error('First name is required.');
      }
      if (isDeactivatingNow && !input?.deactivationStrategy) {
        throw new Error('Please choose how to handle upcoming appointments before deactivating this employee.');
      }

      let deactivationResult: { reassignedCount: number; cancelledCount: number } | null = null;
      let deactivationWarning: string | null = null;
      let attemptedDeactivation = false;
      let employeeBaseForSave = selectedEmployee;
      let regeneratedScheduleCache: Map<string, string> | undefined;
      let recurringBreakRegenerationResult: GenerateRecurringBreakBlockersResult | undefined;

      const nextSocials: Employee['socials'] = {};
      const instagramHandle = normalizeSocialHandle(instagram);
      const facebookLink = facebook.trim();
      const twitterHandle = normalizeSocialHandle(twitter);
      if (showEmployeeSocials) {
        if (instagramHandle) {
          nextSocials.instagram = instagramHandle;
        }
        if (facebookLink) {
          nextSocials.facebook = facebookLink;
        }
        if (twitterHandle) {
          nextSocials.twitter = twitterHandle;
        }
      }

      const updatedEmployee: Employee = {
        ...employeeBaseForSave,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: isResourceMode ? '' : normalizedEmail,
        phone: isResourceMode ? '' : phone.trim(),
        bio: showEmployeeBio
          ? (bio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH) || undefined)
          : employeeBaseForSave.bio,
        socials: showEmployeeSocials ? nextSocials : employeeBaseForSave.socials,
        profilePictureImagePath: showEmployeeProfilePhoto
          ? profilePictureImagePath
          : employeeBaseForSave.profilePictureImagePath,
        admin: isSelectedEmployeeOwner ? true : (isOrchestratorMode ? employeeBaseForSave.admin : isAdmin),
        active,
        recurringBreaks: flattenRecurringBreaks(),
      };
      const hasAnySocial = showEmployeeSocials && Boolean(
        updatedEmployee.socials?.instagram || updatedEmployee.socials?.facebook || updatedEmployee.socials?.twitter
      );
      if (showEmployeeSocials && !hasAnySocial) {
        delete updatedEmployee.socials;
      }

      const nextRecurringBreaks = updatedEmployee.recurringBreaks ?? [];
      const previousRecurringBreaks = employeeBaseForSave.recurringBreaks ?? [];
      const recurringBreaksChanged = !areRecurringBreakSetsEqual(nextRecurringBreaks, previousRecurringBreaks);
      if (workHoursTemplateStatus === 'error' && recurringBreaksChanged) {
        throw new Error('Failed to load employee work hours. Please retry before saving schedule changes.');
      }
      if (isDeactivatingNow) {
        // Run deactivation as the final side-effect after non-destructive saves succeed.
        // This avoids appointment handoff/cancel side effects when earlier validation/persistence fails.
        updatedEmployee.active = employeeBaseForSave.active !== false;
      }

      await updateEmployee(
        employeeBaseForSave,
        updatedEmployee,
        showEmployeeProfilePhoto ? profilePictureFile : undefined,
        tenantId
      );

      const existingWorkHours = employeeWorkHoursQuery.data;
      const hasExistingWorkHours = workHoursTemplateStatus === 'loaded' && !!existingWorkHours;
      const shouldBootstrapWorkHours = workHoursTemplateStatus === 'missing_template';
      const workHoursChanged = hasExistingWorkHours && !areWorkHoursEqual(workHours, existingWorkHours);
      const shouldPersistWorkHours = shouldBootstrapWorkHours || workHoursChanged;

      const shouldRegenerateSchedule = shouldPersistWorkHours || recurringBreaksChanged;

      if (shouldPersistWorkHours) {
        await addEmployeeWorkHours(selectedEmployee.id, workHours, tenantId);
      }

      if (shouldRegenerateSchedule) {
        const scheduleDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, index) => dayjs().add(index, 'day'));
        const baseScheduleCache = queryClient.getQueryData<Map<string, string>>(['employeeSchedule', tenantId, selectedEmployee.id]);
        const mutableScheduleCache = baseScheduleCache ? new Map(baseScheduleCache) : new Map<string, string>();
        const generatedScheduleUpdates = await updateEmployeeSchedulesForDates(selectedEmployee.id, workHours, scheduleDates, tenantId);
        Object.entries(generatedScheduleUpdates).forEach(([dateKey, bitString]) => {
          mutableScheduleCache.set(dateKey, bitString);
        });

        const recurringBreakIds = Array.from(
          new Set([
            ...previousRecurringBreaks.map((definition) => definition.id),
            ...nextRecurringBreaks.map((definition) => definition.id),
          ].filter(Boolean))
        );
        recurringBreakRegenerationResult = await generateRecurringBreakBlockersForEmployee({
          employeeId: selectedEmployee.id,
          windowStart: dayjs(),
          windowEnd: dayjs().add(BUFFER_PERIOD * 2, 'day'),
          siteId: tenantId,
          recurringBreakIdsToReset: recurringBreakIds,
          scheduleBitStrings: mutableScheduleCache,
        });
        regeneratedScheduleCache = mutableScheduleCache;
        updatedEmployee.appointments = recurringBreakRegenerationResult.updatedAppointmentsByDate;
      }

      const services = servicesQuery.data ?? [];
      const currentlyAssigned = new Set(
        services.filter((service) => service.employees.includes(employeeBaseForSave.id)).map((service) => service.id)
      );

      const toAdd = Array.from(selectedServiceIds).filter((id) => !currentlyAssigned.has(id));
      const toRemove = Array.from(currentlyAssigned).filter((id) => !selectedServiceIds.has(id));

      await Promise.all([
        ...toAdd.map((serviceId) =>
          updateDoc(
            doc(db, 'booking-websites', tenantId, 'services', serviceId),
            { employees: arrayUnion(employeeBaseForSave.id) }
          )
        ),
        ...toRemove.map((serviceId) =>
          updateDoc(
            doc(db, 'booking-websites', tenantId, 'services', serviceId),
            { employees: arrayRemove(employeeBaseForSave.id) }
          )
        ),
      ]);

      if (isDeactivatingNow) {
        attemptedDeactivation = true;
        try {
          deactivationResult = await executeEmployeeDeactivation({
            employeeId: selectedEmployee.id,
            strategy: input!.deactivationStrategy!,
            siteId: tenantId,
          });
          // Reflect the known server-side deactivation outcome immediately.
          updatedEmployee.active = false;
          updatedEmployee.bookable = false;
        } catch (error) {
          const reason = (error as Error).message || 'Unknown error';
          deactivationWarning = `Profile and schedule changes were saved, but employee deactivation failed: ${reason}`;
          updatedEmployee.active = employeeBaseForSave.active !== false;
          updatedEmployee.bookable = employeeBaseForSave.bookable;
        }

        if (deactivationResult) {
          try {
            const refreshedEmployee = await fetchEmployeeById(selectedEmployee.id, tenantId);
            if (!refreshedEmployee) {
              deactivationWarning = 'Employee was deactivated, but we could not refresh this profile. Please reopen this employee to verify.';
            } else {
              employeeBaseForSave = refreshedEmployee;
              updatedEmployee.appointments = refreshedEmployee.appointments;
              updatedEmployee.bookable = refreshedEmployee.bookable;
              updatedEmployee.active = refreshedEmployee.active;
            }
          } catch (error) {
            console.error('Failed to refresh employee after deactivation', error);
            deactivationWarning = 'Employee was deactivated, but profile refresh failed. Please reopen this employee to verify.';
          }
        }
      }

      return {
        updatedEmployee,
        toAdd,
        toRemove,
        didPersistWorkHours: shouldPersistWorkHours,
        savedWorkHours: shouldPersistWorkHours ? workHours : undefined,
        regeneratedScheduleCache,
        recurringBreakRegenerationResult,
        deactivationResult,
        deactivationWarning,
        attemptedDeactivation,
      };
    },
    onSuccess: ({
      updatedEmployee,
      toAdd,
      toRemove,
      didPersistWorkHours,
      savedWorkHours,
      regeneratedScheduleCache,
      recurringBreakRegenerationResult,
      deactivationResult,
      deactivationWarning,
      attemptedDeactivation,
    }) => {
      if (deactivationWarning) {
        setSaveSuccessDialogOpen(false);
        setResultSeverity('warning');
        setResultMessage(deactivationWarning);
      } else {
        const successMessage = deactivationResult
          ? `Employee updated and marked inactive. Reassigned ${deactivationResult.reassignedCount}, cancelled ${deactivationResult.cancelledCount} appointment(s).`
          : 'Employee updated successfully.';
        setSaveSuccessMessage(successMessage);
        setSaveSuccessDialogOpen(true);
        setResultMessage(null);
        setResultSeverity(null);
      }
      setOpenDialog(false);
      if (deactivationResult || attemptedDeactivation) {
        setDeactivateDialogOpen(false);
      }
      setSelectedEmployee(updatedEmployee);
      setDeactivatePreviewData(null);
      setDeactivatePreviewError(null);

      if (didPersistWorkHours && savedWorkHours) {
        queryClient.setQueryData<WorkHours>(
          ['manageEmployeeWorkHours', tenantId, updatedEmployee.id],
          savedWorkHours
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ['manageEmployeeWorkHours', tenantId, updatedEmployee.id] });
      }

      applyEmployeeSaveCache(queryClient, {
        updatedEmployee,
        siteId: tenantId,
        regeneratedScheduleCache,
        recurringBreakRegenerationResult,
      });

      if (deactivationResult) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ['appointment', tenantId] }),
          queryClient.invalidateQueries({ queryKey: ['hydrateAppointments', tenantId] }),
          queryClient.invalidateQueries({ queryKey: ['appointmentsByEmployee', tenantId] }),
          queryClient.invalidateQueries({ queryKey: ['employeeCalendarAppointments', tenantId] }),
          queryClient.invalidateQueries({ queryKey: ['employeeSchedule', tenantId] }),
          queryClient.invalidateQueries({ queryKey: ['teamDayAppointments', tenantId] }),
        ]).catch((validationError) => {
          console.error('Failed to validate caches after employee deactivation', validationError);
        });
      }

      if (toAdd.length > 0 || toRemove.length > 0) {
        queryClient.setQueryData<Service[]>(['services', tenantId], (previous = []) => (
          previous.map((service) => {
            if (!toAdd.includes(service.id) && !toRemove.includes(service.id)) {
              return service;
            }

            const employeeSet = new Set(service.employees ?? []);
            if (toAdd.includes(service.id)) {
              employeeSet.add(updatedEmployee.id);
            }
            if (toRemove.includes(service.id)) {
              employeeSet.delete(updatedEmployee.id);
            }

            return {
              ...service,
              employees: Array.from(employeeSet),
            };
          })
        ));
      }
      setNeedsRefreshOnDialogClose(false);
    },
    onError: async (error) => {
      if (isRecurringBreakPartialFailureError(error)) {
        let reconciled = false;
        try {
          await reconcileEmployeeCachesFromSource(queryClient, error.details.employeeId, tenantId);
          reconciled = true;
        } catch (reconcileError) {
          console.error('Failed to reconcile employee caches after partial recurring-break failure', reconcileError);
        }
        if (reconciled) {
          setResultSeverity('warning');
          setResultMessage(
            "We couldn't finish applying all schedule updates, but we refreshed this employee's data. Please review the schedule and save again if needed."
          );
          setNeedsRefreshOnDialogClose(false);
        } else {
          setResultSeverity('error');
          setResultMessage(
            "We couldn't finish updating this employee's schedule, and automatic recovery failed. Please refresh the page and try again."
          );
          setNeedsRefreshOnDialogClose(true);
        }
        setDeactivatePreviewError(
          reconciled
            ? "Some schedule updates did not complete. We refreshed this employee's data."
            : "Schedule update and automatic recovery failed. Please refresh and retry."
        );
        return;
      }

      setResultSeverity('error');
      setResultMessage((error as Error).message || 'Failed to save employee updates.');
      setDeactivatePreviewError((error as Error).message || 'Failed to process deactivation.');
    },
  });

  const deletePreviewMutation = useMutation({
    mutationFn: async (input: { employeeId: string; requestId: number }) => {
      if (!input.employeeId) {
        throw new Error('No employee selected.');
      }
      return previewEmployeeDeletion(input.employeeId, tenantId);
    },
    onSuccess: (preview, variables) => {
      if (variables.requestId !== deletePreviewRequestIdRef.current) {
        return;
      }
      setDeletePreviewData(preview);
      setDeletePreviewError(preview.guardrailError?.message || null);
      setDeleteDialogOpen(true);
    },
    onError: (error, variables) => {
      if (variables.requestId !== deletePreviewRequestIdRef.current) {
        return;
      }
      setDeletePreviewData(null);
      setDeletePreviewError((error as Error).message || 'Failed to prepare employee deletion.');
      setDeleteDialogOpen(true);
    },
  });

  const deactivationPreviewMutation = useMutation({
    mutationFn: async (input: { employeeId: string; requestId: number }) => {
      if (!input.employeeId) {
        throw new Error('No employee selected.');
      }
      return previewEmployeeDeactivation(input.employeeId, tenantId);
    },
    onSuccess: (preview, variables) => {
      if (variables.requestId !== deactivatePreviewRequestIdRef.current) {
        return;
      }
      setDeactivatePreviewData(preview);
      setDeactivatePreviewError(null);
      setDeactivateDialogOpen(true);
    },
    onError: (error, variables) => {
      if (variables.requestId !== deactivatePreviewRequestIdRef.current) {
        return;
      }
      setDeactivatePreviewData(null);
      setDeactivatePreviewError((error as Error).message || 'Failed to prepare deactivation preview.');
      setDeactivateDialogOpen(true);
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: async (cancelUnresolved: boolean) => {
      if (!deleteTargetEmployeeId) {
        throw new Error('No employee selected.');
      }
      return executeEmployeeDeletion({
        employeeId: deleteTargetEmployeeId,
        cancelUnresolved,
        siteId: tenantId,
      });
    },
    onSuccess: (result) => {
      const employeeName = deleteTargetEmployeeName || 'Employee';
      const deletionSummary = `${employeeName} deleted. Reassigned ${result.reassignedCount}, cancelled ${result.cancelledCount}, removed ${result.deletedBlockersCount} blocker(s).`;
      const successMessage = result.authDisableFailed
        ? `${deletionSummary} Profile data was removed, but login could not be disabled. Disable this user in Firebase Auth manually.`
        : deletionSummary;
      setDeleteSuccessMessage(successMessage);
      setDeleteSuccessDialogOpen(true);
      setResultMessage(null);
      setResultSeverity(null);
      setDeleteDialogOpen(false);
      setDeleteConfirmInput('');
      setDeletePreviewData(null);
      setDeletePreviewError(null);
      setDeleteTargetEmployeeId(null);
      setDeleteTargetEmployeeName('');
      setNeedsRefreshOnDialogClose(false);
      setOpenDialog(false);
      setSelectedEmployee(null);
    },
    onError: (error) => {
      const deletionError = error as Error & { unresolvedAppointments?: Appointment[] };
      if (Array.isArray(deletionError.unresolvedAppointments) && deletionError.unresolvedAppointments.length > 0) {
        setDeletePreviewData((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            unresolvedAppointments: deletionError.unresolvedAppointments!,
          };
        });
      }
      setResultSeverity('error');
      setResultMessage(deletionError.message || `Failed to delete ${resourceSingular.toLowerCase()}.`);
      setDeletePreviewError(deletionError.message || `Failed to delete ${resourceSingular.toLowerCase()}.`);
    },
  });

  const isLoading = employeesQuery.isLoading || servicesQuery.isLoading || businessConfigQuery.isLoading;
  const isError = employeesQuery.isError || servicesQuery.isError || businessConfigQuery.isError;
  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true;
  const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
  const showEmployeeBio = bookingUxConfig.uiFlags.showEmployeeBio;
  const showEmployeeSocials = bookingUxConfig.uiFlags.showEmployeeSocials;
  const showEmployeeProfilePhoto = bookingUxConfig.uiFlags.showEmployeeProfilePhoto;
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const ownerId = businessConfigQuery.data?.paymentInfo?.owner || '';
  const primaryOrchestratorId = businessConfigQuery.data?.preferences?.primaryOrchestratorId || '';
  const isSelectedEmployeeOwner = selectedEmployee?.id === ownerId;
  const currentEmployeeId = currentEmployeeQuery.data?.id;
  const isSelectedEmployeeCurrentUser = selectedEmployee?.id === currentEmployeeId;

  const employees = employeesQuery.data ?? [];
  const services = servicesQuery.data ?? [];
  const sortedEmployees = useMemo(() => {
    const filteredEmployees = isResourceMode
      ? employees.filter((employee) => employee.id !== ownerId)
      : employees;
    if (!primaryOrchestratorId) {
      return filteredEmployees;
    }

    return [...filteredEmployees].sort((left, right) => {
      const leftIsPrimary = left.id === primaryOrchestratorId ? 1 : 0;
      const rightIsPrimary = right.id === primaryOrchestratorId ? 1 : 0;
      if (leftIsPrimary !== rightIsPrimary) {
        return rightIsPrimary - leftIsPrimary;
      }
      return `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`);
    });
  }, [employees, isResourceMode, ownerId, primaryOrchestratorId]);

  const dialogTitle = selectedEmployee
    ? `Manage ${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : `Manage ${resourcePlural}`;
  const deleteConfirmationTarget = deleteTargetEmployeeName;
  const deleteHasUnresolved = (deletePreviewData?.unresolvedAppointments.length ?? 0) > 0;
  const canConfirmDelete = deleteConfirmInput.trim() === deleteConfirmationTarget;
  const deactivationReassignableCount = deactivatePreviewData?.reassignments.length ?? 0;
  const deactivationUnresolvedCount = deactivatePreviewData?.unresolvedAppointments.length ?? 0;
  const canHandoffAll = deactivationUnresolvedCount === 0;
  const fullNameValue = `${firstName} ${lastName}`.trim();

  const handleOpenDeleteDialog = () => {
    if (!selectedEmployee || isSelectedEmployeeCurrentUser) {
      return;
    }
    const nextRequestId = deletePreviewRequestIdRef.current + 1;
    deletePreviewRequestIdRef.current = nextRequestId;
    const fullName = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim();
    setDeleteTargetEmployeeId(selectedEmployee.id);
    setDeleteTargetEmployeeName(fullName);
    setDeleteDialogOpen(true);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    deletePreviewMutation.mutate({
      employeeId: selectedEmployee.id,
      requestId: nextRequestId,
    });
  };

  const handleCloseDeleteDialog = () => {
    deletePreviewRequestIdRef.current += 1;
    setDeleteDialogOpen(false);
    setDeleteConfirmInput('');
    setDeletePreviewData(null);
    setDeletePreviewError(null);
    setDeleteTargetEmployeeId(null);
    setDeleteTargetEmployeeName('');
  };

  const handleOpenDeactivationDialog = () => {
    if (!selectedEmployee) {
      return;
    }
    const nextRequestId = deactivatePreviewRequestIdRef.current + 1;
    deactivatePreviewRequestIdRef.current = nextRequestId;
    setDeactivateDialogOpen(true);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
    deactivationPreviewMutation.mutate({
      employeeId: selectedEmployee.id,
      requestId: nextRequestId,
    });
  };

  const handleCloseDeactivationDialog = () => {
    deactivatePreviewRequestIdRef.current += 1;
    setDeactivateDialogOpen(false);
    setDeactivatePreviewData(null);
    setDeactivatePreviewError(null);
  };

  const handleCloseDeleteSuccessDialog = () => {
    setDeleteSuccessDialogOpen(false);
    window.location.reload();
  };

  const refreshAfterRecoveryFailure = (employeeId: string) => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ['allEmployees', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['employees', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['services', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['manageEmployeeWorkHours', tenantId, employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employeeSchedule', tenantId, employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['appointmentsByEmployee', tenantId, employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employeeCalendarAppointments', tenantId, employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['hydrateAppointments', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['appointment', tenantId] }),
    ]).catch((refreshError) => {
      console.error('Failed to refresh employee caches after recovery failure', refreshError);
    });
  };

  const handleCloseManageDialog = () => {
    const employeeIdToRefresh = selectedEmployee?.id ?? previousDialogEmployeeIdRef.current;
    setOpenDialog(false);
    if (!needsRefreshOnDialogClose) {
      return;
    }
    setNeedsRefreshOnDialogClose(false);
    if (employeeIdToRefresh) {
      refreshAfterRecoveryFailure(employeeIdToRefresh);
    }
  };

  const handleSaveAttempt = () => {
    if (!selectedEmployee) {
      return;
    }
    setSaveSuccessDialogOpen(false);
    const deactivating = selectedEmployee.active !== false && active === false;
    if (deactivating) {
      handleOpenDeactivationDialog();
      return;
    }
    saveMutation.mutate(undefined);
  };

  useEffect(() => {
    if (!deleteEmployeeMutation.isPending) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [deleteEmployeeMutation.isPending]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <Alert severity="error">{`Failed to load ${resourcePlural.toLowerCase()} data.`}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', pt: { xs: 1, sm: 2 } }}>
      <LoadingOverlay
        open={deleteEmployeeMutation.isPending}
        title={`Deleting ${resourceSingular.toLowerCase()}…`}
        detail="Keep this page open until it finishes."
      />
      <LoadingOverlay
        open={saveMutation.isPending}
        title="Saving changes…"
        detail="This only takes a moment."
      />
      {resultMessage && resultSeverity && (
        <Alert severity={resultSeverity} sx={{ mb: 2 }}>
          {resultMessage}
        </Alert>
      )}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end', mb: 3 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/add-employee')}
          sx={{ borderRadius: 3, fontWeight: 900, px: 2.5 }}>
          {`Add ${resourceSingular.toLowerCase()}`}
        </Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 12, sm: 4 }, display: 'grid', gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
        {sortedEmployees.map((employee) => {
          const name = `${employee.firstName} ${employee.lastName}`.trim();
          const available = employee.active !== false;
          const serviceCount = services.filter((service) => service.employees.includes(employee.id)).length;
          const openEmployee = () => {
            setSelectedEmployee(employee);
            setOpenDialog(true);
          };
          return (
            <Card key={employee.id} elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'rgba(15,23,42,.08)',
              boxShadow: '0 10px 30px rgba(15,23,42,.06)', opacity: available ? 1 : .72,
              transition: 'transform 160ms ease, box-shadow 160ms ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 16px 40px rgba(15,23,42,.10)' } }}>
              <CardActionArea onClick={openEmployee} sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={1.75} alignItems="center">
                  <EmployeeAvatar employee={employee} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={850} noWrap>{name || 'Unnamed'}</Typography>
                    {!isResourceMode ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {employee.email || employee.phone || 'No contact details'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {serviceCount} {serviceCount === 1 ? 'service' : 'services'}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.75 }}>
                  <Chip size="small" label={available ? 'Available' : 'Not available'} color={available ? 'success' : 'default'}
                    variant={available ? 'filled' : 'outlined'} sx={{ fontWeight: 800 }} />
                  {employee.id === ownerId ? <Chip size="small" label="Owner" sx={{ fontWeight: 800 }} /> :
                    employee.admin ? <Chip size="small" label="Admin" sx={{ fontWeight: 800 }} /> : null}
                  {employee.id === primaryOrchestratorId ? (
                    <Chip size="small" icon={<StarIcon />} label="Gets booking alerts" color="warning" variant="outlined"
                      sx={{ fontWeight: 800 }} />
                  ) : null}
                  {!isResourceMode ? (
                    <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
                      label={`${serviceCount} ${serviceCount === 1 ? 'service' : 'services'}`} />
                  ) : null}
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      <Portal>
        <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'fixed', left: 0, right: 0, bottom: 0,
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2, px: 2, pt: 1.25, pb: 'max(14px, env(safe-area-inset-bottom))',
          bgcolor: 'rgba(255,255,255,.96)', borderTop: '1px solid rgba(15,23,42,.10)', backdropFilter: 'blur(16px)' }}>
          <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/add-employee')}
            sx={{ minHeight: 50, borderRadius: 3, fontWeight: 900 }}>
            {`Add ${resourceSingular.toLowerCase()}`}
          </Button>
        </Box>
      </Portal>

      <Dialog
        open={openDialog}
        onClose={handleCloseManageDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: editDialogPaperSx(isMobile) }}
      >
        <Box sx={{ position: 'relative', px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 },
          '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 5,
            background: 'linear-gradient(90deg, #1976d2 0%, #44a3ff 52%, #74d4ff 100%)' } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {selectedEmployee ? <EmployeeAvatar employee={selectedEmployee} /> : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" component="h2" fontWeight={900} noWrap>
                {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim() || dialogTitle : dialogTitle}
              </Typography>
              <Typography color="text.secondary">{`Edit this ${resourceSingular.toLowerCase()}`}</Typography>
            </Box>
            <IconButton onClick={handleCloseManageDialog} aria-label="Close"><CloseIcon /></IconButton>
          </Stack>
          <Tabs value={expandedManageSection ?? 'profile'} onChange={(_, value: ManageSectionKey) => setExpandedManageSection(value)}
            variant="fullWidth" sx={{ mt: 1.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 850 } }}>
            <Tab value="profile" label="Details" />
            <Tab value="services" label="Services" />
            <Tab value="work_hours" label="Availability" />
          </Tabs>
        </Box>
        <DialogContent dividers sx={{ ...editDialogContentSx, px: { xs: 2.5, sm: 3.5 }, py: 2.5 }}>
          {selectedEmployee && (
            <Stack spacing={2.5}>
              {(expandedManageSection ?? 'profile') === 'profile' ? (
                <Stack spacing={2}>
                  {showEmployeeProfilePhoto && (
                    <Box>
                      <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing=".08em">
                        {isResourceMode ? 'Photo (optional)' : 'Profile photo (optional)'}
                      </Typography>
                      <ImageSelect
                        initialImagePath={selectedEmployee.profilePictureImagePath}
                        onImageChange={handleProfilePictureChange}
                        allowDelete
                        styleType="circle"
                        altText={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                      />
                    </Box>
                  )}
                  {isResourceMode ? (
                    <TextField
                      label="Name"
                      fullWidth
                      value={fullNameValue}
                      onChange={(event) => {
                        const parsed = parseFullName(event.target.value);
                        setFirstName(parsed.firstName);
                        setLastName(parsed.lastName);
                      }}
                    />
                  ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="First name" fullWidth value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                      <TextField label="Last name" fullWidth value={lastName} onChange={(event) => setLastName(event.target.value)} />
                    </Stack>
                  )}
                  {showEmployeeBio && (
                    <TextField
                      label={isResourceMode ? 'Description (optional)' : 'Bio (optional)'}
                      fullWidth
                      multiline
                      minRows={3}
                      value={bio}
                      onChange={(event) => setBio(event.target.value.slice(0, EMPLOYEE_BIO_MAX_LENGTH))}
                      placeholder={isResourceMode ? 'What customers should know about it.' : 'A short introduction customers see on your website.'}
                      helperText={`${bio.length}/${EMPLOYEE_BIO_MAX_LENGTH}`}
                      inputProps={{ maxLength: EMPLOYEE_BIO_MAX_LENGTH }}
                    />
                  )}
                  {!isResourceMode && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Email"
                        fullWidth
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={selectedEmployee.isOrchestrated !== true}
                        helperText={selectedEmployee.isOrchestrated !== true ? 'Used to sign in, so it can’t be changed here.' : undefined}
                      />
                      <TextField label="Phone" fullWidth value={phone} onChange={(event) => setPhone(event.target.value)} />
                    </Stack>
                  )}
                  {showEmployeeSocials && (
                    <Stack spacing={2}>
                      <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing=".08em">Social (optional)</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField label="Instagram" fullWidth value={instagram} onChange={(event) => setInstagram(event.target.value)}
                          placeholder="username" InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ mr: .5 }}>@</Typography> }} />
                        <TextField label="X (Twitter)" fullWidth value={twitter} onChange={(event) => setTwitter(event.target.value)}
                          placeholder="username" InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ mr: .5 }}>@</Typography> }} />
                      </Stack>
                      <TextField label="Facebook page link" fullWidth value={facebook} onChange={(event) => setFacebook(event.target.value)}
                        placeholder="https://facebook.com/yourpage" />
                    </Stack>
                  )}
                  {!isResourceMode && (
                    <SwitchRow
                      label="Admin access"
                      detail={isSelectedEmployeeOwner
                        ? 'The owner always has admin access.'
                        : `Can manage ${resourcePlural.toLowerCase()}, services and appointments.`}
                      checked={isSelectedEmployeeOwner ? true : isAdmin}
                      disabled={isOrchestratorMode || isSelectedEmployeeOwner}
                      onChange={setIsAdmin}
                    />
                  )}
                  <SwitchRow
                    label="Available for booking"
                    detail={active ? 'Customers can book them.' : 'Hidden from customers until you turn this on.'}
                    checked={active}
                    onChange={setActive}
                  />
                </Stack>
              ) : null}

              {expandedManageSection === 'services' ? (
                services.length ? (
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Customers can book them for the selected services.</Typography>
                      <Button size="small" onClick={() => {
                        const allSelected = services.every((service) => selectedServiceIds.has(service.id));
                        services.forEach((service) => {
                          if (allSelected === selectedServiceIds.has(service.id)) toggleService(service.id);
                        });
                      }}>
                        {services.every((service) => selectedServiceIds.has(service.id)) ? 'Clear' : 'Select all'}
                      </Button>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {services.map((service) => {
                        const selected = selectedServiceIds.has(service.id);
                        return (
                          <Chip key={service.id} clickable aria-pressed={selected}
                            label={`${service.title} · ${formatMinutes(service.time)}`}
                            color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'}
                            onClick={() => toggleService(service.id)}
                            sx={{ height: 38, borderRadius: 999, fontWeight: 700 }} />
                        );
                      })}
                    </Box>
                  </Box>
                ) : <Alert severity="info">No services yet. Add services first, then assign them here.</Alert>
              ) : null}

              {expandedManageSection === 'work_hours' ? (
                <Stack spacing={1}>
                  {workHoursTemplateStatus === 'error' ? (
                    <Alert severity="warning">
                      Availability couldn’t be loaded right now. You can still save details and services.
                    </Alert>
                  ) : DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => {
                    const parsed = extractTimesFromBitString(workHours[day]);
                    return (
                      <ScheduleCard
                        key={day}
                        day={day}
                        initialWorkHours={parsed}
                        onScheduleChange={handleScheduleChange}
                        recurringBreaks={recurringBreakTemplates[day]}
                        onRecurringBreaksChange={handleRecurringBreaksChange}
                        templateEndDate={templateEndDate}
                      />
                    );
                  })}
                </Stack>
              ) : null}

              {resultMessage && resultSeverity && (
                <Alert severity={resultSeverity}>
                  {resultMessage}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2.5, sm: 3.5 }, py: 1.75,
          pb: { xs: 'max(14px, env(safe-area-inset-bottom))', sm: 1.75 } }}>
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleOpenDeleteDialog}
            disabled={
              saveMutation.isPending ||
              deletePreviewMutation.isPending ||
              deleteEmployeeMutation.isPending ||
              !selectedEmployee ||
              isSelectedEmployeeCurrentUser
            }
            sx={{ fontWeight: 800 }}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseManageDialog} sx={{ fontWeight: 800 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAttempt}
            disabled={
              saveMutation.isPending ||
              deleteEmployeeMutation.isPending ||
              deactivationPreviewMutation.isPending ||
              !selectedEmployee ||
              isWorkHoursLoadingForSelection
            }
            sx={{ minHeight: 46, px: 3, borderRadius: 3, fontWeight: 900 }}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={deleteEmployeeMutation.isPending ? undefined : handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {`Delete ${resourceSingular}`}
          <IconButton
            color="secondary"
            onClick={handleCloseDeleteDialog}
            disabled={deleteEmployeeMutation.isPending}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {deletePreviewMutation.isPending ? (
            <Typography variant="body2" color="text.secondary">Preparing deletion preview...</Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                {`This permanently removes the ${resourceSingular.toLowerCase()} profile and related records.`}
              </Alert>

              {deletePreviewError && (
                <Alert severity="error">{deletePreviewError}</Alert>
              )}

              {deletePreviewData && !deletePreviewData.guardrailError && (
                <>
                  <DialogContentText>
                    Review the impact before deleting <strong>{deleteConfirmationTarget}</strong>.
                  </DialogContentText>

                  <Chip
                    color="info"
                    label={`${deletePreviewData.reassignments.length} upcoming appointment(s) will be reassigned`}
                  />

                  <TextField
                    label={`Type "${deleteConfirmationTarget}" to confirm`}
                    value={deleteConfirmInput}
                    onChange={(event) => setDeleteConfirmInput(event.target.value)}
                    fullWidth
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteEmployeeMutation.isPending}>
            Close
          </Button>
          {deletePreviewData && !deletePreviewData.guardrailError && (
            <Button
              variant="contained"
              color="error"
              disabled={deleteEmployeeMutation.isPending || !canConfirmDelete}
              onClick={() => deleteEmployeeMutation.mutate(deleteHasUnresolved)}
            >
              {deleteEmployeeMutation.isPending
                ? 'Deleting...'
                : deleteHasUnresolved
                  ? 'Cancel Unresolved & Delete'
                  : `Delete ${resourceSingular}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialogOpen}
        onClose={saveMutation.isPending ? undefined : handleCloseDeactivationDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {`Mark ${resourceSingular} Inactive`}
          <IconButton
            color="secondary"
            onClick={handleCloseDeactivationDialog}
            disabled={saveMutation.isPending}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {deactivationPreviewMutation.isPending ? (
            <Typography variant="body2" color="text.secondary">Preparing appointment impact...</Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                This employee will be marked as inactive and removed from future booking availability.
              </DialogContentText>

              {deactivatePreviewError && (
                <Alert severity="error">{deactivatePreviewError}</Alert>
              )}

              {deactivatePreviewData && (
                <>
                  <Chip
                    color="info"
                    label={`${deactivationReassignableCount} upcoming appointment(s) can be handed off`}
                  />
                  <Chip
                    color={canHandoffAll ? 'success' : 'warning'}
                    label={`${deactivationUnresolvedCount} upcoming appointment(s) cannot be handed off automatically`}
                  />
                  {!canHandoffAll && (
                    <Alert severity="warning">
                      Hand off requires all upcoming appointments to be transferable.
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivationDialog} disabled={saveMutation.isPending}>
            Close
          </Button>
          <Button
            variant="outlined"
            disabled={
              saveMutation.isPending ||
              deactivationPreviewMutation.isPending ||
              !deactivatePreviewData ||
              !canHandoffAll
            }
            onClick={() => saveMutation.mutate({ deactivationStrategy: 'handoff' })}
          >
            {saveMutation.isPending ? 'Applying...' : 'Hand Off Appointments'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saveMutation.isPending || deactivationPreviewMutation.isPending || !deactivatePreviewData}
            onClick={() => saveMutation.mutate({ deactivationStrategy: 'cancel' })}
          >
            {saveMutation.isPending ? 'Applying...' : 'Cancel Appointments'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveSuccessDialogOpen}
        onClose={() => setSaveSuccessDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{saveSuccessMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSaveSuccessDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSuccessDialogOpen}
        onClose={handleCloseDeleteSuccessDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{`${resourceSingular} Deleted`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteSuccessMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDeleteSuccessDialog}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageEmployeesPage;
